/**
 * The agent's activity, slowed to a pace a person can follow (Adam, 13 Sep:
 * the status text changed forty times in a minute and could not be read).
 *
 * Raw activity statuses arrive many times a second. This keeps a short log
 * of what the turn has done, holds each line for at least a second before
 * the next replaces it, drops repeats, and holds the orb's state for a
 * little longer still, so a turn that flicks between preparing and running
 * a tool reads as one steady orb. Everything clears when the turn ends.
 */
import { useEffect, useRef, useState } from 'react'
import type { ActivityStatus } from '../types.js'
import { orbStateFor, RESTING_ORB_STATE, type OrbState } from '../components/ActivityOrb.js'

export interface ActivityLogEntry {
  id: number
  /** ms since the log started */
  at: number
  label: string
  detail?: string
  category?: ActivityStatus['category']
  toolName?: string
  toolInput?: Record<string, unknown>
  elapsedSeconds?: number
}

export interface ActivityLog {
  /** The line in force; null between turns, when the log is the last turn's. */
  current: ActivityLogEntry | null
  orbState: OrbState
  log: ActivityLogEntry[]
}

export const ACTIVITY_LINE_HOLD_MS = 1000
export const ACTIVITY_ORB_HOLD_MS = 1500
export const ACTIVITY_LOG_LENGTH = 8
const FALLBACK_LABEL = 'Working…'
const KEY_SEP = '|'

const EMPTY: ActivityLog = { current: null, orbState: RESTING_ORB_STATE, log: [] }

export function useActivityLog(status: ActivityStatus | null | undefined, active: boolean): ActivityLog {
  const [state, setState] = useState<ActivityLog>(EMPTY)
  const startedAt = useRef<number | null>(null)
  const lastLineAt = useRef(0)
  const lastOrbAt = useRef(0)
  const lastKey = useRef('')
  const nextId = useRef(1)
  const lineTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const orbTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(status)
  latest.current = status

  const present = Boolean(status)
  const label = status?.label || (status ? FALLBACK_LABEL : '')
  const detail = status?.detail ?? ''
  const category = status?.category
  const toolName = status?.toolName
  const elapsed = status?.elapsedSeconds

  // A new line: the latest status, no sooner than a second after the last.
  // The effect reacts to the status's words, not to the object identity,
  // which changes on every tick.
  useEffect(() => {
    if (!active || !present) {
      if (lineTimer.current) clearTimeout(lineTimer.current)
      if (orbTimer.current) clearTimeout(orbTimer.current)
      lineTimer.current = orbTimer.current = null
      startedAt.current = null
      lastLineAt.current = lastOrbAt.current = 0
      lastKey.current = ''
      // Between turns: the last turn's log stays for the hover, the orb rests.
      setState(s => (s.current === null && s.orbState === RESTING_ORB_STATE ? s : { ...s, current: null, orbState: RESTING_ORB_STATE }))
      return undefined
    }
    const key = label + KEY_SEP + detail
    if (key === lastKey.current) {
      // Same line, later tick: only the clock moved.
      if (elapsed != null) {
        setState(s => (s.current && s.current.elapsedSeconds !== elapsed ? { ...s, current: { ...s.current, elapsedSeconds: elapsed } } : s))
      }
      return undefined
    }
    const commit = () => {
      lineTimer.current = null
      const s = latest.current
      if (!s) return
      const now = Date.now()
      const fresh = startedAt.current === null
      const started = startedAt.current ?? now
      startedAt.current = started
      lastLineAt.current = now
      const entryLabel = s.label || FALLBACK_LABEL
      const entryKey = entryLabel + KEY_SEP + (s.detail ?? '')
      if (entryKey === lastKey.current) return
      lastKey.current = entryKey
      const entry: ActivityLogEntry = {
        id: nextId.current++, at: now - started, label: entryLabel, detail: s.detail,
        category: s.category, toolName: s.toolName, toolInput: s.toolInput, elapsedSeconds: s.elapsedSeconds,
      }
      setState(prev => ({ ...prev, current: entry, log: [...(fresh ? [] : prev.log), entry].slice(-ACTIVITY_LOG_LENGTH) }))
    }
    const wait = Math.max(0, lastLineAt.current + ACTIVITY_LINE_HOLD_MS - Date.now())
    if (lineTimer.current) clearTimeout(lineTimer.current)
    if (lastLineAt.current === 0) commit()
    else lineTimer.current = setTimeout(commit, wait)
    return undefined
  }, [active, present, label, detail, category, toolName, elapsed])

  // The orb follows the current line, held for at least a second and a half.
  const wanted: OrbState = state.current ? orbStateFor(state.current) : RESTING_ORB_STATE
  const hasCurrent = Boolean(state.current)
  useEffect(() => {
    if (!hasCurrent || wanted === state.orbState) return undefined
    const wait = Math.max(0, lastOrbAt.current + ACTIVITY_ORB_HOLD_MS - Date.now())
    if (orbTimer.current) clearTimeout(orbTimer.current)
    const commit = () => {
      orbTimer.current = null
      lastOrbAt.current = Date.now()
      setState(s => (s.current ? { ...s, orbState: orbStateFor(s.current) } : s))
    }
    if (lastOrbAt.current === 0) commit()
    else orbTimer.current = setTimeout(commit, wait)
    return undefined
  }, [wanted, state.orbState, hasCurrent])

  useEffect(() => () => {
    if (lineTimer.current) clearTimeout(lineTimer.current)
    if (orbTimer.current) clearTimeout(orbTimer.current)
  }, [])

  return state
}
